/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');


const CourseSearchScreen = ({ navigation }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { id: 'All', name: 'All' },
    { id: 'Civil', name: 'Civil' },
    { id: 'Mechanical', name: 'Mechanical' },
    { id: 'Electrical', name: 'Electrical' },
    { id: 'Computer', name: 'Computer' },
    { id: 'Chemical', name: 'Chemical' },
    { id: 'Safety', name: 'Safety' },
    { id: 'Management', name: 'Management' },
  ];

  // ── courses from /api/data/courses (replaces mock array) ──
  // Assumes table `courses` with columns: id, title, instructor, category,
  // price, originalPrice, rating, students, duration, level, image, description.
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        const token = (await AsyncStorage.getItem('auth_token'))
                  || (await AsyncStorage.getItem('userToken'))
                  || (await AsyncStorage.getItem('token')) || '';
        const res = await fetch('https://api.jobeng.ai/api/data/courses?limit=200', {
          headers: {
            'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json.data || json.results || []);
        if (mounted) setCourses(list);
      } catch (e) { console.warn('[CourseSearchScreen] fetch courses failed:', e.message); }
      finally { if (mounted) setCoursesLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      Alert.alert('Search', `Searching for "${searchQuery}"...`);
    }
  };

  const handleSort = () => {
    const sortOptions = [
      { label: 'Relevance', value: 'relevance' },
      { label: 'Price (Low to High)', value: 'price_asc' },
      { label: 'Price (High to Low)', value: 'price_desc' },
      { label: 'Rating (High to Low)', value: 'rating' },
      { label: 'Most Popular', value: 'popular' },
    ];

    Alert.alert(
      'Sort Courses',
      'Choose sorting option:',
      [
        ...sortOptions.map(option => ({
          text: option.label,
          onPress: () => setSortBy(option.value)
        })),
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const handleFilter = () => {
    Alert.alert(
      'Filter Options',
      'Choose filter criteria:',
      [
        { text: 'All Courses', onPress: () => setActiveCategory('All') },
        { text: 'Free Courses', onPress: () => filterByPrice('free') },
        { text: 'Under £100', onPress: () => filterByPrice('under100') },
        { text: 'Under £200', onPress: () => filterByPrice('under200') },
        { text: 'Beginner Level', onPress: () => filterByLevel('Beginner') },
        { text: 'Intermediate Level', onPress: () => filterByLevel('Intermediate') },
        { text: 'Advanced Level', onPress: () => filterByLevel('Advanced') },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const filterByPrice = (type) => {
    Alert.alert('Filter Applied', `Showing courses ${type}`);
  };

  const filterByLevel = (level) => {
    Alert.alert('Filter Applied', `Showing ${level} level courses`);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={12} color="#fdb913" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={12} color="#fdb913" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={12} color="#e0e0e0" />
      );
    }

    return stars;
  };

  const renderCourseCard = ({ item }) => (
    <TouchableOpacity
      style={styles.courseCard}
      onPress={() => navigation.navigate('CourseDetailsScreen', { courseId: item.id, course: item })}
    >
      {item.isFeatured && (
        <View style={styles.featuredBadge}>
          <Text style={styles.featuredText}>Featured</Text>
        </View>
      )}
      
      <View style={[styles.courseIconContainer, { backgroundColor: item.color }]}>
        <MaterialCommunityIcons name={item.icon} size={32} color="#ffffff" />
      </View>
      
      <View style={styles.courseInfo}>
        <Text style={styles.courseTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.courseInstructor} numberOfLines={1}>
          by {item.instructor}
        </Text>
        
        <View style={styles.ratingContainer}>
          <View style={styles.starsContainer}>
            {renderStars(item.rating)}
          </View>
          <Text style={styles.ratingText}>
            {item.rating} ({item.students.toLocaleString()})
          </Text>
        </View>
        
        <View style={styles.metaContainer}>
          <Text style={styles.levelText}>{item.level}</Text>
          <Text style={styles.durationText}>{item.duration}</Text>
        </View>
        
        <View style={styles.priceContainer}>
          {item.originalPrice > item.price && (
            <Text style={styles.originalPrice}>£{item.originalPrice}</Text>
          )}
          <Text style={styles.currentPrice}>£{item.price}</Text>
        </View>
        
        {item.offer && (
          <View style={styles.offerBadge}>
            <Text style={styles.offerText}>{item.offer}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  const renderCategoryItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.categoryItem,
        activeCategory === item.id && styles.activeCategoryItem,
      ]}
      onPress={() => setActiveCategory(item.id)}
    >
      <Text
        style={[
          styles.categoryText,
          activeCategory === item.id && styles.activeCategoryText,
        ]}
      >
        {item.name}
      </Text>
    </TouchableOpacity>
  );

  // Filter courses based on search and category
  const filteredCourses = courses.filter(course => {
    const matchesCategory = activeCategory === 'All' || course.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      String(course.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.category ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return a.price - b.price;
      case 'price_desc':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'popular':
        return b.students - a.students;
      default:
        return 0;
    }
  });

  const renderHeader = () => (
    <View>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={20}
          color="#64748B"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search courses, instructors, topics..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Categories */}
      <View style={styles.categoriesContainer}>
        <FlatList
          data={categories}
          renderItem={renderCategoryItem}
          keyExtractor={item => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled={true}
        />
      </View>

      {/* Results Header */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsText}>
          {filteredCourses.length} courses found
          {activeCategory !== 'All' && ` in ${activeCategory}`}
          {searchQuery && ` for "${searchQuery}"`}
        </Text>
        <TouchableOpacity style={styles.sortButton} onPress={handleSort}>
          <Ionicons name="swap-vertical" size={16} color="#0e4e96" />
          <Text style={styles.sortText}>Sort</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Search Courses</Text>
        <TouchableOpacity style={styles.filterButton} onPress={handleFilter}>
          <Ionicons name="options-outline" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Main Content */}
      <FlatList
        data={filteredCourses}
        renderItem={renderCourseCard}
        keyExtractor={item => item.id}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        numColumns={1}
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="magnify" size={64} color="#64748B" />
            <Text style={styles.emptyTitle}>No courses found</Text>
            <Text style={styles.emptyText}>
              Try adjusting your search terms or filters
            </Text>
            <TouchableOpacity 
              style={styles.clearButton} 
              onPress={() => {
                setSearchQuery('');
                setActiveCategory('All');
              }}
            >
              <Text style={styles.clearButtonText}>Clear Search</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  filterButton: {
    padding: 5,
  },
  contentContainer: {
    padding: 15,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 10,
    paddingHorizontal: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: '#333',
  },
  categoriesContainer: {
    marginBottom: 15,
  },
  categoryItem: {
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
  },
  activeCategoryItem: {
    backgroundColor: '#0e4e96',
  },
  categoryText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  activeCategoryText: {
    color: '#fff',
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  sortText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
    fontWeight: '500',
  },
  courseCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
    overflow: 'hidden',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: '#fdb913',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    zIndex: 1,
  },
  featuredText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  courseIconContainer: {
    width: '100%',
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  courseInfo: {
    padding: 15,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 13,
    color: '#64748B',
    marginBottom: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#64748B',
  },
  metaContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  levelText: {
    fontSize: 12,
    color: '#19abb5',
    fontWeight: 'bold',
  },
  durationText: {
    fontSize: 12,
    color: '#64748B',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  originalPrice: {
    fontSize: 14,
    color: '#94A3B8',
    textDecorationLine: 'line-through',
    marginRight: 8,
  },
  currentPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  offerBadge: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  offerText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  clearButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CourseSearchScreen;

